import {Badge} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconInfoCircle} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {
  IntegrationTokenCreateResponse,
  IntegrationTokenResponse,
} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {
  useGetIntegrationTokensQuery,
  useRevokeIntegrationTokenMutation,
} from 'src/redux/queries/integrationTokens';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {formatRelativeOrNever} from 'src/shared/utils/formatters';
import {TokenRevealModal} from '../components/TokenRevealModal';
import {CreateIntegrationTokenModal} from '../components/CreateIntegrationTokenModal';

export interface InstanceIntegrationsTabProps {
  instanceId: number;
}

const formatRelativeTime = (date: Date | undefined): React.ReactNode => {
  if (!date) {
    return (
      <Text size="sm" c="dimmed">
        Never
      </Text>
    );
  }

  const relative = formatRelativeOrNever(date);
  const absolute = String(date).toLongDate();

  return (
    <Tooltip label={absolute} withArrow>
      <Text size="sm" style={{cursor: 'default'}}>
        {relative}
      </Text>
    </Tooltip>
  );
};

export const InstanceIntegrationsTab: React.FC<
  InstanceIntegrationsTabProps
> = ({instanceId}) => {
  const {setAlert} = useAlert();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] =
    useState<IntegrationTokenResponse | null>(null);
  const [revealToken, setRevealToken] =
    useState<IntegrationTokenCreateResponse | null>(null);
  const [revealModalOpen, setRevealModalOpen] = useState(false);

  const {data, isLoading, isError, isFetching, refetch} =
    useGetIntegrationTokensQuery(
      {instanceId},
      {refetchOnMountOrArgChange: true}
    );

  const [revokeToken, {isLoading: isRevoking}] =
    useRevokeIntegrationTokenMutation();

  const rows = data ?? [];

  const columns = useMemo<MRT_ColumnDef<IntegrationTokenResponse>[]>(
    () => [
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        maxSize: 400,
        Cell: ({row}) =>
          row.original.description ? (
            <Text size="sm" lineClamp={1}>
              {row.original.description}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: row => (row.isActive ? 'Active' : 'Revoked'),
        Cell: ({row}) =>
          row.original.isActive ? (
            <Badge color="green" variant="light">
              Active
            </Badge>
          ) : (
            <Badge color="gray" variant="light">
              Revoked
            </Badge>
          ),
      },
      {
        id: 'createdAt',
        accessorFn: row => (row.createdAt ? new Date(row.createdAt) : null),
        filterVariant: 'date',
        header: 'Created',
        Cell: ({row}) => (
          <>
            {row.original.createdAt
              ? String(row.original.createdAt).toShortDate()
              : '—'}
          </>
        ),
      },
      {
        id: 'lastUsedAt',
        header: 'Last Used',
        accessorFn: row => (row.lastUsedAt ? new Date(row.lastUsedAt) : null),
        Cell: ({row}) => formatRelativeTime(row.original.lastUsedAt),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'instance-integrations-tab',
    columns,
    defaultColumnVisibility: {},
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 10,
    },
    defaultSorting: [{id: 'createdAt', desc: true}],
    defaultShowColumnFilters: false,
  });

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    try {
      await revokeToken({
        id: revokeTarget.id,
        instanceId: revokeTarget.instanceId!,
      }).unwrap();
      const name = revokeTarget.description ?? `Token #${revokeTarget.id}`;
      setAlert(`"${name}" has been revoked`, 'success');
      setRevokeTarget(null);
      void refetch();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setRevokeTarget(null);
    }
  };

  const handleTokenCreated = (token: IntegrationTokenCreateResponse) => {
    setCreateModalOpen(false);
    setRevealToken(token);
    setRevealModalOpen(true);
  };

  const handleRevealAcknowledge = () => {
    setRevealModalOpen(false);
    setRevealToken(null);
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          {row.original.isActive && (
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                color="red"
                onClick={() => setRevokeTarget(row.original)}>
                Revoke
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          )}
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <TableToolbar.Action
              variant="create"
              onClick={() => setCreateModalOpen(true)}>
              New Token
            </TableToolbar.Action>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount: rows.length,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  const revokeTargetName = revokeTarget
    ? revokeTarget.description ?? `Token #${revokeTarget.id}`
    : '';

  return (
    <>
      <PageHeader>
        <PageHeader.Title subtitle="Manage access tokens for external systems">
          Integration Tokens
        </PageHeader.Title>
      </PageHeader>

      {!isLoading && rows.length === 0 && (
        <Alert
          variant="light"
          color="blue"
          icon={<IconInfoCircle />}
          title="Getting Started with Integration Tokens"
          mb="md">
          <Text size="sm">
            One token authenticates an external system. Identity providers such
            as Okta, Azure AD, and OneLogin use it to provision users over SCIM.
            Connected applications use it to read data such as the inspections
            feed. Create a token to get started.
          </Text>
        </Alert>
      )}

      {table}

      <CreateIntegrationTokenModal
        opened={createModalOpen}
        instanceId={instanceId}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleTokenCreated}
      />

      <TokenRevealModal
        opened={revealModalOpen}
        token={revealToken}
        onAcknowledge={handleRevealAcknowledge}
      />

      <ConfirmDialog
        opened={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
        title="Revoke Integration Token?"
        body={`This will permanently disable the token '${revokeTargetName}'. Any external system using this token will stop working immediately. This cannot be undone.`}
        confirmationButtonColor="red"
        buttonTextYes="Revoke Token"
        buttonTextNo="Cancel"
        confirmationButtonDisabled={isRevoking}
      />
    </>
  );
};
